import { MARTIAL_STATUSES } from 'Enum';
import {
  emailMessage,
  emailWithEmpty,
  isValidIsraeliID,
  phoneNumberMessage,
  requiredMessage,
  requiredPhoneMessage,
  requiredPrefixMessage,
  textLengthMustBe,
  textTooLong,
  validateIsraeliIdMessage,
} from 'Regex';
import * as yup from 'yup';

interface IContactDetailsDirtyState {
  phonePrefix?: boolean | undefined;
  phoneNumber?: boolean | undefined;
  extraPhoneNumber?: boolean | undefined;
  extraPhonePrefix?: boolean | undefined;
}

export const getContactDetailsSchema = (
  dirtyState: IContactDetailsDirtyState
) => {
  return yup
    .object()
    .shape({
      phonePrefix: yup
        .number()
        .test('isRequired', requiredPrefixMessage, (value) => {
          if (dirtyState.phoneNumber) {
            return value !== -1;
          }
          return true;
        }),
      phoneNumber: yup.string().when('isRequired', {
        is: () => dirtyState.phonePrefix || dirtyState.phoneNumber,
        then: () =>
          yup
            .string()
            .required(requiredPhoneMessage)
            .min(7, phoneNumberMessage)
            .max(7, phoneNumberMessage),
        otherwise: () => yup.string(),
      }),
      extraPhonePrefix: yup
        .number()
        .test('isRequired', requiredPrefixMessage, (value) => {
          if (dirtyState.extraPhoneNumber) {
            return value !== -1;
          }
          return true;
        }),
      extraPhoneNumber: yup.string().when('isRequired', {
        is: () => dirtyState.extraPhonePrefix || dirtyState.extraPhoneNumber,
        then: () =>
          yup
            .string()
            .required(requiredPhoneMessage)
            .min(7, phoneNumberMessage)
            .max(7, phoneNumberMessage),
        otherwise: () => yup.string(),
      }),
      email: yup
        .string()
        .matches(emailWithEmpty, emailMessage)
        .max(50, textTooLong(50)),
    })
    .nullable();
};

export const getMaritalStatusSchema = (
  isDirty: boolean,
  status: MARTIAL_STATUSES
) => {
  return yup
    .object()
    .shape({
      status: yup.number().test('isRequired', requiredMessage, (value) => {
        if (isDirty) {
          return value !== -1;
        }
        return true;
      }),
      lastUpdatedDate: yup.date().when('isRequired', {
        is: () => isDirty,
        then: () =>
          yup
            .date()
            .typeError('')
            .required(requiredMessage)
            .nullable()
            .default(undefined),
        otherwise: () => yup.date().nullable().default(undefined),
      }),
      spouse: yup.object().shape({
        identityNumber: yup.string().when('isRequired', {
          is: () => isDirty,
          then: () =>
            yup
              .string()
              .required(requiredMessage)
              .test(
                'isValidIsraeliID',
                validateIsraeliIdMessage,
                (value: any) => (value === '' ? true : isValidIsraeliID(value))
              ),
          otherwise: () => yup.string(),
        }),
        firstName: yup.string().when('isRequired', {
          is: () => isDirty,
          then: () => yup.string().required(requiredMessage),
        }),
        lastName: yup.string(),
        birthDate: yup.date().when('isRequired', {
          is: () => isDirty,
          then: () =>
            yup
              .date()
              .typeError('')
              .required(requiredMessage)
              .nullable()
              .default(undefined),
          otherwise: () => yup.date().nullable().default(undefined),
        }),
        gender: yup.number(),
      }),
    })
    .nullable();
};

export const getAddressSchema = (
  isDirty: boolean,
  isDifferentAddressForMail: boolean
) =>
  yup
    .object()
    .shape({
      addresses: yup.array().of(
        yup.object().shape({
          city: yup.number().when('isAbroad', {
            is: (value: boolean) => !value && isDirty,
            then: () =>
              yup
                .number()
                .test('isRequired', requiredMessage, (v: any, index: any) =>
                  v === undefined
                    ? !(index.path.includes('[0]') || isDifferentAddressForMail)
                    : true
                ),
            otherwise: () => yup.number(),
          }),
          abroadCity: yup.string().when('isAbroad', {
            is: (value: boolean) => value && isDirty,
            then: () => yup.string().required(requiredMessage),
            otherwise: () => yup.string(),
          }),
          street: yup.string().when('isMailBox', {
            is: (value: boolean) => !value && isDirty,
            then: () =>
              yup
                .string()
                .test('isRequired', requiredMessage, (value: any, index: any) =>
                  value === ''
                    ? !(index.path.includes('[0]') || isDifferentAddressForMail)
                    : true
                ),
            otherwise: () => yup.string(),
          }),
          houseNum: yup.string(),
          apartmentNum: yup.string(),
          zipCode: yup.string().when('isRequired', {
            is: () => isDirty,
            then: () =>
              yup
                .string()
                .test('isRequired', requiredMessage, (v: any, index: any) =>
                  v === ''
                    ? !(index.path.includes('[0]') || isDifferentAddressForMail)
                    : true
                )
                .test(
                  'zipCodeLength',
                  textLengthMustBe(7),
                  (v: any, index: any) =>
                    index.path.includes('[0]') || isDifferentAddressForMail
                      ? v.length === 7
                      : true
                ),
            otherwise: () => yup.string(),
          }),
          mailBox: yup.string().when('isMailBox', {
            is: (value: boolean) => value && isDifferentAddressForMail,
            then: () => yup.string().required(requiredMessage),
            otherwise: () => yup.string(),
          }),
          country: yup.string().when('isAbroad', {
            is: (value: boolean) => value && isDirty,
            then: () =>
              yup
                .string()
                .test('isRequired', requiredMessage, (v: any, index: any) =>
                  v === ''
                    ? !(index.path.includes('[0]') || isDifferentAddressForMail)
                    : true
                ),
            otherwise: () => yup.string(),
          }),
          isAbroad: yup.boolean().nullable(),
          isMailBox: yup.boolean().nullable(),
        })
      ),
    })
    .nullable();

export const getFamilyMembersSchema = (isDirty: boolean) =>
  yup.object().shape({
    familyMembers: yup.array().of(
      yup.object().shape({
        identityNumber: yup
          .string()
          .test('isValidIsraeliID', validateIsraeliIdMessage, (value: any) =>
            isDirty
              ? isValidIsraeliID(value)
              : value === ''
              ? true
              : isValidIsraeliID(value)
          )
          .nullable(),
        firstName: yup.string().nullable(),
        lastName: yup.string().nullable(),
        birthDate: yup.date().when('isRequired', {
          is: () => isDirty,
          then: () =>
            yup
              .date()
              .typeError('')
              .required(requiredMessage)
              .nullable()
              .default(undefined),
          otherwise: () => yup.date().nullable().default(undefined),
        }),
        gender: yup.number(),
      })
    ),
  });
